/**
 * MasterDataContext
 * ─────────────────
 * In-memory cache for master data that's frequently read across many pages
 * (machines, workers, operators, items). Each master is fetched ONCE per
 * session/auth and re-used by every consumer until refresh() is called or
 * the user logs out.
 *
 * Why a context (vs. React Query)?
 *   • No new dependency
 *   • Same cache shared by every page — switching tabs no longer re-fetches
 *   • Tiny API surface: useMasterData() → { machines, workers, operators, items, refresh }
 *
 * Pages that mutate master data (add/edit/delete a machine, worker, etc.)
 * should call refresh('machines' | 'workers' | 'operators' | 'items') after
 * a successful save so other pages see the change without a full page reload.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MasterDataContext = createContext(null);

const ENDPOINTS = {
  machines: '/api/machines',
  workers: '/api/workers',
  operators: '/api/operators',
  items: '/api/items',
};

// 5-min stale window: refetch automatically if a consumer mounts after this.
const STALE_MS = 5 * 60 * 1000;

export function MasterDataProvider({ children }) {
  const { user } = useAuth() || {};
  const [data, setData] = useState({ machines: [], workers: [], operators: [], items: [] });
  const [loading, setLoading] = useState({ machines: false, workers: false, operators: false, items: false });
  const fetchedAt = useRef({ machines: 0, workers: 0, operators: 0, items: 0 });
  // Track in-flight requests so concurrent useMasterData() consumers de-dupe.
  const inflight = useRef({});

  const fetchOne = useCallback(async (key, force = false) => {
    if (!user) return;
    const fresh = Date.now() - fetchedAt.current[key] < STALE_MS;
    if (!force && fresh && data[key].length) return;
    if (inflight.current[key]) return inflight.current[key];
    setLoading(l => ({ ...l, [key]: true }));
    const p = axios.get(`${API_URL}${ENDPOINTS[key]}`, { withCredentials: true })
      .then(res => {
        setData(d => ({ ...d, [key]: res.data || [] }));
        fetchedAt.current[key] = Date.now();
        return res.data || [];
      })
      .catch(() => [])
      .finally(() => {
        setLoading(l => ({ ...l, [key]: false }));
        inflight.current[key] = null;
      });
    inflight.current[key] = p;
    return p;
  }, [user, data]);

  // Fetch all four masters when login state changes. Depend on user id (stable string)
  // — not the user object — so route navigations that reload /api/auth/me don't re-trigger.
  const userId = user?.id || user?._id || user?.username || null;
  useEffect(() => {
    if (!userId) {
      setData({ machines: [], workers: [], operators: [], items: [] });
      fetchedAt.current = { machines: 0, workers: 0, operators: 0, items: 0 };
      return;
    }
    Object.keys(ENDPOINTS).forEach(k => fetchOne(k));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Refresh one or all masters. Page calls refresh('machines') after a CRUD save.
  const refresh = useCallback((key) => {
    if (!key) {
      return Promise.all(Object.keys(ENDPOINTS).map(k => fetchOne(k, true)));
    } else if (ENDPOINTS[key]) {
      return fetchOne(key, true);
    }
  }, [fetchOne]);

  const value = useMemo(() => ({
    machines: data.machines,
    workers: data.workers,
    operators: data.operators,
    items: data.items,
    loading,
    refresh,
  }), [data, loading, refresh]);

  return <MasterDataContext.Provider value={value}>{children}</MasterDataContext.Provider>;
}

export function useMasterData() {
  const ctx = useContext(MasterDataContext);
  if (!ctx) {
    // Fallback (provider absent): empty arrays, no-op refresh — keeps pages crash-safe.
    return { machines: [], workers: [], operators: [], items: [], loading: {}, refresh: () => {} };
  }
  return ctx;
}
